import React from "react";
import {
  QrCode,
  Users,
  MapPin,
  MousePointerClick,
} from "lucide-react";

const QR_URL =
  "https://customer-assets.emergentagent.com/job_tactiq-campaigns/artifacts/ylvryzhf_WhatsApp%20Image%202026-07-09%20at%202.02.02%20PM.jpeg";

const METRICS = [
  {
    icon: QrCode,
    label: "Total Scans",
    desc: "Every time a bottle is scanned — anywhere, any device.",
  },
  {
    icon: Users,
    label: "Unique Visitors",
    desc: "Real, distinct humans reached — not just repeat scans.",
  },
  {
    icon: MousePointerClick,
    label: "Conversion Rate",
    desc: "% of scans that took action — signed up, ordered, called.",
  },
  {
    icon: MapPin,
    label: "Scan Location",
    desc: "City-level heatmap of where your brand traveled.",
  },
];

export const Analytics = () => {
  return (
    <section
      id="analytics"
      data-testid="analytics-section"
      className="relative py-24 sm:py-32 bg-[var(--tq-navy)] text-white overflow-hidden"
    >
      {/* Grid background */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Heading row with QR on the right */}
        <div className="grid lg:grid-cols-12 gap-10 items-center mb-16">
          <div className="lg:col-span-8">
            <div className="overline mb-4" style={{ color: "#93C5FD" }}>
              QR Analytics · Control Room
            </div>
            <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter leading-[0.92]">
              Every bottle.
              <br />
              <span className="font-editorial italic font-medium">
                Now measurable.
              </span>
            </h2>
            <p className="mt-6 text-white/70 max-w-xl text-lg">
              Add your QR Code to any label and every scan becomes data. Below is
              a sample of the signals we capture — real-time, per campaign.
            </p>
          </div>

          {/* QR image on right */}
          <div className="lg:col-span-4 flex lg:justify-end">
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-white/5 blur-2xl" />
              <div
                data-testid="qr-image-card"
                className="relative bg-white rounded-3xl p-5 sm:p-6 shadow-2xl w-[220px] sm:w-[260px]"
              >
                <img
                  src={QR_URL}
                  alt="Tactiq trackable QR code"
                  className="w-full h-auto block"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {METRICS.map((m, i) => {
            const Icon = m.icon;
            return (
              <div
                key={m.label}
                data-testid={`metric-${i}`}
                className="group relative bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-6 backdrop-blur transition-all duration-300 hover:bg-white/[0.08] hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-white">
                    <Icon size={18} />
                  </div>
                  <span className="font-mono text-[10px] text-white/40">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold tracking-tight mb-1.5">
                  {m.label}
                </h3>
                <p className="text-xs sm:text-[13px] text-white/60 leading-relaxed">
                  {m.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
